<script setup lang="ts">
/**
 * One labelled line of the form. Owns the layout and the look of every control
 * placed in it, so the pickers themselves carry no styling of their own.
 */
defineProps<{
  label: string
  /** Id of the control the label names. */
  forId: string
  /** Keeps the label for screen readers only, for a row continuing the one above. */
  hideLabel?: boolean | undefined
}>()
</script>

<template>
  <div class="form-row">
    <label class="form-row__label" :class="{ 'visually-hidden': hideLabel }" :for="forId">
      {{ label }}
    </label>
    <div class="form-row__controls">
      <slot />
    </div>
    <slot name="after" />
  </div>
</template>

<style scoped>
.form-row {
  display: grid;
  grid-template-columns: 9.5rem minmax(0, 1fr);
  gap: var(--space-1) var(--space-4);
  align-items: center;
}

/* Continuation rows keep their controls in the second column. */
.form-row__controls {
  grid-column: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.form-row__label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

/* On a phone the label sits above its controls. */
@media (max-width: 36rem) {
  .form-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .form-row__controls {
    grid-column: 1;
  }

  /* "1 × <a long option name>" stays on one line: the wide select gives up
     width rather than wrapping away from its count. The open picker shows the
     full names either way. */
  .form-row :deep(select:not(.count-select)) {
    flex: 1 1 0;
    min-width: 0;
  }
}

.form-row :deep(select),
.form-row :deep(input) {
  max-width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
}

.form-row :deep(select) {
  cursor: pointer;
}

.form-row :deep(select:hover),
.form-row :deep(input:hover) {
  border-color: var(--color-border-strong);
}

.form-row :deep(input[aria-invalid='true']) {
  border-color: var(--color-danger);
}

/* Spans the row under the controls, so it never squeezes them when it appears. */
.form-row :deep(.form-row__error) {
  grid-column: 1 / -1;
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-danger);
}

.form-row :deep(.form-row__muted) {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
